import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Plus, CheckCircle2, Circle, FileText, ImageIcon, Trash2, ExternalLink } from "lucide-react";
import api, { API_BASE } from "../lib/api";
import Avatar from "../components/Avatar";
import FileUpload from "../components/FileUpload";
import CommentThread from "../components/CommentThread";
import { useAuth } from "../context/AuthContext";
import { formatDateTime, formatFileSize, relativeTime } from "../lib/format";

export default function MeetingDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [m, setM] = useState(null);
  const [members, setMembers] = useState([]);
  const [newItem, setNewItem] = useState({ title: "", assignee_id: "", due_date: "" });
  const [notes, setNotes] = useState("");

  const load = useCallback(async () => {
    const [meeting, mem] = await Promise.all([api.get(`/meetings/${id}`), api.get("/members")]);
    setM(meeting.data);
    setNotes(meeting.data.notes || "");
    setMembers(mem.data);
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!m) return <div className="text-[#9A9590]">Loading…</div>;

  const addItem = async () => {
    if (!newItem.title.trim() || !newItem.assignee_id) return;
    const { data } = await api.post(`/meetings/${id}/action-items`, newItem);
    setM(data);
    setNewItem({ title: "", assignee_id: "", due_date: "" });
  };
  const toggleItem = async (itemId) => {
    const { data } = await api.patch(`/meetings/${id}/action-items/${itemId}`);
    setM(data);
  };
  const saveNotes = async () => {
    const { data } = await api.patch(`/meetings/${id}`, { notes });
    setM(data);
  };

  const onAttUploaded = async () => { await load(); };
  const deleteAtt = async (fileId) => {
    if (!window.confirm("Remove this attachment?")) return;
    await api.delete(`/uploads/files/${fileId}`);
    await load();
  };
  const attUrl = (fileId) =>
    `${API_BASE}/uploads/files/${fileId}`;
  const isImage = (ct) => (ct || "").startsWith("image/");
  const isAttendee = (m.attendee_ids || []).includes(user.id) || user.role === "admin";

  return (
    <div className="space-y-8" data-testid="meeting-detail-page">
      <Link to="/meetings" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#9A9590] hover:text-[#C9A84C] transition">
        <ArrowLeft size={14} /> Back to meetings
      </Link>

      <div className="ts-card p-8 sm:p-10">
        <div className="overline">Meeting</div>
        <h1 className="font-serif text-3xl sm:text-4xl text-[#F0EDE8] mt-3 tracking-tight">{m.title}</h1>
        <div className="text-sm text-[#9A9590] mt-3">{formatDateTime(m.date)} · {m.location || "Location TBD"}</div>

        {m.agenda && (
          <div className="mt-6">
            <div className="overline mb-2">Agenda</div>
            <pre className="text-sm text-[#F0EDE8] whitespace-pre-wrap font-sans leading-relaxed">{m.agenda}</pre>
          </div>
        )}

        <div className="mt-6">
          <div className="overline mb-2">Attendees</div>
          <div className="flex flex-wrap gap-3">
            {(m.attendees || []).map(a => (
              <div key={a.id} className="flex items-center gap-2 ts-card px-3 py-1.5 border-[#C9A84C]/15">
                <Avatar name={a.name} photoUrl={a.photo_url} color={a.avatar_color} size={24} />
                <span className="text-xs text-[#F0EDE8]">{a.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="ts-card p-7">
        <div className="overline mb-4">Action items</div>
        <ul className="space-y-3" data-testid="action-items-list">
          {(m.action_items || []).map(ai => {
            const isMine = ai.assignee_id === user.id;
            const canToggle = isMine || user.role === "admin";
            return (
              <li key={ai.id} className="flex items-center gap-3">
                <button onClick={() => canToggle && toggleItem(ai.id)} disabled={!canToggle} data-testid={`action-toggle-${ai.id}`}>
                  {ai.status === "done" ? <CheckCircle2 size={20} className="text-[#C9A84C]"/> : <Circle size={20} className="text-[#9A9590]"/>}
                </button>
                <div className={`flex-1 ${ai.status === "done" ? "text-[#9A9590] line-through" : "text-[#F0EDE8]"}`}>{ai.title}</div>
                <div className="flex items-center gap-2 text-xs text-[#9A9590]">
                  <Avatar name={ai.assignee?.name} photoUrl={ai.assignee?.photo_url} color={ai.assignee?.avatar_color} size={22} />
                  <span className="hidden sm:inline">{ai.assignee?.name}</span>
                </div>
              </li>
            );
          })}
          {(!m.action_items || m.action_items.length === 0) && (
            <li className="text-sm text-[#9A9590]">No action items yet.</li>
          )}
        </ul>
        {user.role === "admin" && (
          <div className="mt-5 pt-5 border-t border-[#C9A84C]/12 flex flex-col sm:flex-row gap-2">
            <input className="ts-input flex-1" placeholder="New action item" value={newItem.title} onChange={(e)=>setNewItem({...newItem,title:e.target.value})} data-testid="ai-title"/>
            <select className="ts-input sm:w-48" value={newItem.assignee_id} onChange={(e)=>setNewItem({...newItem,assignee_id:e.target.value})} data-testid="ai-assignee">
              <option value="">Assign to…</option>
              {members.map(mem => <option key={mem.id} value={mem.id}>{mem.name}</option>)}
            </select>
            <input type="date" className="ts-input sm:w-40" value={newItem.due_date} onChange={(e)=>setNewItem({...newItem,due_date:e.target.value})}/>
            <button onClick={addItem} className="btn-gold rounded-md px-4 text-sm inline-flex items-center gap-2"><Plus size={14}/>Add</button>
          </div>
        )}
      </div>

      <div className="ts-card p-7" data-testid="meeting-attachments">
        <div className="flex items-center justify-between mb-4">
          <div className="overline">Attachments</div>
          {isAttendee && (
            <FileUpload
              endpoint={`/uploads/meeting/${id}/attachment`}
              accept="application/pdf,image/jpeg,image/png,image/webp"
              maxBytes={10 * 1024 * 1024}
              variant="document"
              buttonLabel="Upload attachment"
              onUploaded={onAttUploaded}
              dataTestid="meeting-att-upload"
            />
          )}
        </div>
        <ul className="space-y-2" data-testid="meeting-attachments-list">
          {(m.attachments || []).map((a) => (
            <li key={a.file_id} className="flex items-center gap-3 p-3 rounded-md border border-[#C9A84C]/12 hover:border-[#C9A84C]/30 transition group">
              <div className="shrink-0 w-9 h-9 rounded bg-[#1A1A1A] border border-[#C9A84C]/20 flex items-center justify-center text-[#C9A84C]">
                {isImage(a.content_type) ? <ImageIcon size={16} /> : <FileText size={16} />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-[#F0EDE8] truncate">{a.original_filename}</div>
                <div className="text-[10px] uppercase tracking-widest text-[#9A9590] mt-0.5">
                  {formatFileSize(a.size)} · {a.uploader?.name || "Unknown"} · {relativeTime(a.uploaded_at)}
                </div>
              </div>
              <a href={attUrl(a.file_id)} target="_blank" rel="noreferrer" className="text-[#C9A84C] hover:text-[#E1C97A] opacity-0 group-hover:opacity-100 transition" title="Open" data-testid={`att-open-${a.file_id}`}>
                <ExternalLink size={14} />
              </a>
              {(a.uploaded_by === user.id || user.role === "admin") && (
                <button onClick={() => deleteAtt(a.file_id)} className="text-[#9A9590] hover:text-red-400 opacity-0 group-hover:opacity-100 transition" title="Remove" data-testid={`att-delete-${a.file_id}`}>
                  <Trash2 size={14} />
                </button>
              )}
            </li>
          ))}
          {(!m.attachments || m.attachments.length === 0) && (
            <li className="text-sm text-[#9A9590]">No attachments yet.</li>
          )}
        </ul>
      </div>

      <div className="ts-card p-7">
        <div className="overline mb-3">Meeting notes</div>
        <textarea
          value={notes}
          onChange={(e)=>setNotes(e.target.value)}
          rows={6}
          disabled={user.role !== "admin"}
          placeholder="What was decided…"
          className="ts-input resize-none py-2"
        />
        {user.role === "admin" && (
          <button onClick={saveNotes} className="btn-gold rounded-md px-5 py-2.5 text-sm mt-4">Save notes</button>
        )}
      </div>

      <div className="ts-card p-7" data-testid="meeting-discussion">
        <div className="overline mb-4">Discussion ({m.comments?.length || 0})</div>
        <CommentThread
          apiPrefix={`/meetings/${id}`}
          comments={m.comments || []}
          currentUser={user}
          onUpdated={setM}
          canPost={isAttendee}
          placeholder="Note for the meeting…"
          emptyHint="No comments yet."
          forbiddenHint="Only invited attendees can comment on this meeting."
        />
      </div>
    </div>
  );
}
